import { AlertTriangle } from "lucide-react";
import { navCivilDate, formatCivilDate } from "@/lib/funds/nav-freshness";

type FundDisclaimerProps = {
  /**
   * The NAV date shown on this page (RM-W1-11). When supplied, the data paragraph names that exact
   * date; when it is absent or unusable, the paragraph points at the date printed beside the NAV
   * instead. Either way the page makes no promise about how recently the value was refreshed, which
   * is what the removed "lag of up to 24 hours" sentence did.
   */
  navDate?: string | Date | null;
};

export function FundDisclaimer({ navDate }: FundDisclaimerProps) {
  const civil = navCivilDate(navDate);

  return (
    <div className="bg-surface border border-surface-light/30 rounded-xl p-5 mt-8">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-gold shrink-0 mt-0.5" />
        <div>
          <h4 className="text-foreground text-sm font-semibold mb-2">
            Important Disclaimer
          </h4>
          <div className="text-dim text-xs leading-relaxed space-y-2">
            <p>
              Mutual fund investments are subject to market risks. Read all
              scheme-related documents carefully before investing. Past
              performance is not indicative of future returns.
            </p>
            <p>
              The information provided on WhatsTheMoat is for educational and
              informational purposes only and should not be construed as
              financial, investment, or tax advice. WhatsTheMoat is not a SEBI
              registered investment advisor, research analyst, or portfolio
              manager.
            </p>
            <p>
              The analysis, scores, and recommendations generated by our AI
              engine are based on publicly available data and proprietary
              algorithms. They do not constitute a buy, sell, or hold
              recommendation for any mutual fund scheme. Always consult a
              qualified financial advisor before making investment decisions.
            </p>
            <p>
              NAV, AUM, returns, and other data points are sourced from public
              APIs.{" "}
              {civil ? (
                <>
                  The NAV on this page is the value published for{" "}
                  {formatCivilDate(civil)}, and the freshness badge beside it
                  says how old that is.
                </>
              ) : (
                <>
                  Every NAV on this page is shown with the date it is published
                  for, and the freshness badge beside it says how old that date
                  is.
                </>
              )}{" "}
              There is no guaranteed refresh interval: the data is only as
              current as the date shown. WhatsTheMoat does not guarantee the
              accuracy, completeness, or timeliness of any data displayed.
            </p>
            <p className="text-dim/70">
              AMFI Registration No. is not applicable. WhatsTheMoat is a
              technology platform, not a mutual fund distributor.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
